/**
 * The town vendor panel's contract, its words, and the two decisions it is
 * allowed to make.
 *
 * ## Not one price is produced anywhere in this client
 *
 * Three numbers appear on the vendor panel and every one of them is the API's:
 *
 *   - **what the vendor asks** is stated by `GET /api/characters/:id/vendor` and
 *     carried through untouched;
 *   - **what the vendor bids** and **what a repair costs** are stated by
 *     `GET /api/characters/:id/vendor/quote` — the carried things, priced by the
 *     same process that will charge for them;
 *   - **what an exchange actually came to** is the API's own `spent` or
 *     `received`, off the answer, and it is what every sentence below quotes.
 *
 * The second of those used to be `@d2/core`'s own two pricing functions, called
 * in `./vendor-view.ts`. That was one definition shared rather than a second
 * copy of a rule — the client genuinely ran the server's code — but the number a
 * player read was still worked out on a different machine from the one that
 * would charge them, and nothing forced the two to agree. Nothing in this package
 * prices anything now: grep it for either function's name, or for a multiplier,
 * and there is none.
 *
 * There is likewise no comparison against a purse and no `held − price` anywhere
 * in the package. That last one is the arithmetic this project has already banned
 * once, for the death toll: *"subtracting one from the other would produce a
 * balance nobody owns"* (`town-screen.tsx`). The reasoning is identical here and
 * the seam is narrower — an exchange answers with the **whole new character**, so
 * the purse a player reads is the server's answer and not a client's guess about
 * it.
 *
 * ## The panel does not decide what will be refused
 *
 * Deliberately unlike `skill-tree-model.ts`, which blocks a cell whose only
 * outcome is a refusal. It can: an empty point pool and a maxed skill are bounds
 * on numbers the panel is already showing. Affordability is not — *"can this
 * purse cover this price"* is `gold.ts`'s `spend`, and a client that answered it
 * would be a second copy of a rule whose first copy is the one that charges.
 * So Buy stays live at any balance, the server refuses, and the panel says *not
 * enough gold* in words. One rule, one place, and the refusal is a sentence
 * rather than a greyed button with nothing to say for itself.
 *
 * ## One press is one exchange
 *
 * `startExchange` is the latch, and it is the caller's rather than a component's
 * because the caller is what holds the request. A double press is a **second
 * purchase**: the API is atomic, so what is at risk is not the database, it is
 * the player's gold. The panel disables its controls while one is in flight as
 * well, and the two are not redundant — the disabled control stops the press,
 * this stops the *request*, and one of them is still standing when a keystroke,
 * a caller that forgot to pass `pending`, or a re-render between the two puts a
 * second dispatch through.
 */

import { type Durability } from '@d2/core';

import { type ApiRefusal, type VendorRefusalReason } from '../account/api';
import { countLabel } from './town-screen-model';

/** One thing in the shop window, at the price the API stated. */
export interface OfferRow {
  /** The catalogue id a purchase names. Never the id the bought copy ends up with. */
  readonly offerId: string;
  readonly name: string;
  /** What the vendor asks, whole, exactly as the API stated it. */
  readonly price: number;
}

/** One thing in the pack, with what the vendor bids for it. */
export interface SaleRow {
  readonly itemId: string;
  readonly name: string;
  /**
   * What the vendor said it would pay, off the quote route.
   *
   * A quote and not a promise: it is what a sale **would** fetch, read when the
   * counter was drawn. What one **did** fetch is `VendorNote`'s `received`, off
   * the answer, and if the two ever disagree that is the one that happened —
   * and the fresh quote arrives on the same answer, so the row is replaced in
   * the render that reports the charge.
   */
  readonly bid: number;
}

/** One damaged thing, with what mending it costs. */
export interface RepairRow {
  readonly itemId: string;
  readonly name: string;
  /** What the vendor said mending it would cost, off the quote route. A quote, as `SaleRow.bid` is. */
  readonly cost: number;
  /** Whether it is being worn. Worn gear is what wears, and it cannot be sold. */
  readonly worn: boolean;
  /** What is left and what it had. Data off the item, not a rule about it. */
  readonly durability: Durability;
}

/** Everything the panel draws: the counter, the pack, and what is damaged. */
export interface VendorView {
  readonly stock: readonly OfferRow[];
  readonly sellable: readonly SaleRow[];
  readonly damaged: readonly RepairRow[];
}

/** The three things a player can ask the vendor for. */
export type ExchangeKind = 'buy' | 'sell' | 'repair';

/** The one exchange in flight: which verb, and which id it named. */
export interface VendorPending {
  readonly kind: ExchangeKind;
  /** An offer id for a purchase, an item id for a sale or a repair. */
  readonly id: string;
}

/**
 * How the last exchange turned out.
 *
 * The three successes carry the **API's own figure** and the name of the thing
 * the client pressed. That split is the point: this client knows what it asked
 * for, and only the server knows what it charged.
 */
export type VendorNote =
  | { readonly kind: 'bought'; readonly name: string; readonly spent: number }
  | { readonly kind: 'sold'; readonly name: string; readonly received: number }
  | { readonly kind: 'repaired'; readonly name: string; readonly spent: number }
  | { readonly kind: 'refused'; readonly refusal: ApiRefusal };

export const bought = (name: string, spent: number): VendorNote => ({ kind: 'bought', name, spent });
export const sold = (name: string, received: number): VendorNote => ({ kind: 'sold', name, received });
export const repaired = (name: string, spent: number): VendorNote => ({ kind: 'repaired', name, spent });

/**
 * A refusal, carried as a value rather than as a sentence.
 *
 * The same call `joinRefused` makes one screen over: the caller knows what the
 * API said, this module decides what a player is told about it. A caller that
 * passed prose would be a caller that could put *"unauthenticated"* over a shop
 * counter.
 */
export const vendorRefused = (refusal: ApiRefusal): VendorNote => ({ kind: 'refused', refusal });

export interface VendorPanelProps {
  /** What the vendor and the character have, or `null` while it is being read. */
  readonly view: VendorView | null;
  /** Why there is no view, when there is none and it is not merely late. */
  readonly failure?: ApiRefusal | null;
  readonly onBuy: (offerId: string) => void;
  readonly onSell: (itemId: string) => void;
  readonly onRepair: (itemId: string) => void;
  /**
   * The exchange in flight, or `null`. **One at a time**, and the caller owns
   * it: a latch inside the panel would outlive the caller's knowledge of the
   * request and leave a dead button on a screen with a purse on it.
   */
  readonly pending?: VendorPending | null;
  /** How the last exchange came to, as a value. See `vendorRefused`. */
  readonly note?: VendorNote | null;
}

/** Said instead of a proportion of a purse nobody asked this panel to compute. */
export const ASKING_NOTE = 'Asking the vendor…';

/** Said while the counter is being read. */
export const READING_NOTE = 'Reading the vendor…';

export const NOTHING_TO_SELL = 'Nothing in your pack. The vendor buys what you are carrying.';

export const NOTHING_DAMAGED = 'Nothing you are carrying is damaged.';

export const NOTHING_FOR_SALE = 'The vendor has nothing for you today.';

/**
 * Said out loud, because it is the one thing this version leaves out that the
 * API would have allowed.
 *
 * The vendor will sell out of the stash — `vendor.service.ts` looks in both
 * grids — and this panel offers only the pack. A stash a player can sell out of
 * but cannot see, sort or move an item into is a one-way door, and the door is
 * missing on purpose: there is no route between a stash and a pack yet, and the
 * stash's own shape is still open (`TODO.md` #25).
 */
export const STASH_NOTE = 'Your stash is not on the counter — the vendor buys out of your pack.';

/** What the vendor will not buy, said where a player would otherwise go looking for it. */
export const WORN_NOTE = 'Worn gear is mended here but never sold; take it off first.';

/**
 * What a player is told when the vendor says no.
 *
 * Not one of them repeats the API's own word. `insufficient-gold` is a wire
 * token; *"not enough gold"* is a sentence, and the difference is the whole
 * reason the refusal is carried as a value and turned into words here.
 *
 * Each names **what a player can do about it**, because that is what separates
 * the ones that look alike: an empty purse is fixed by selling, a full one by
 * spending, and a full pack by selling something *first*. `purse-full` says the
 * item was kept, because `sell` refuses the whole exchange rather than paying
 * part of it — a player who is not told that will assume the item is gone.
 */
export const describeVendorRefusal = (reason: VendorRefusalReason): string => {
  switch (reason) {
    case 'insufficient-gold':
      return 'Not enough gold for that. Sell something first, or come back after a run.';
    case 'inventory-full':
      return 'Your pack is full. Sell something to make room, then buy it.';
    case 'purse-full':
      return 'Your purse is already full, so that sale was refused whole — you keep the item. Spend some gold first.';
    case 'no-such-item':
      return 'That is not in your pack any more. Worn gear has to come off before it can be sold.';
    case 'unknown-offer':
      return 'The vendor is not selling that. Leave town and come back to see what is on the counter now.';
    case 'nothing-to-repair':
      return 'That is not damaged, so there is nothing to mend.';
    case 'not-repairable':
      return 'That never wears, so there is nothing to mend.';
    case 'conflict':
      return 'Somebody else moved this character while the vendor was counting. Nothing changed — try again.';
  }
};

/**
 * What a player is told about anything else that came back.
 *
 * The four kinds below the vendor's own are the ones a vendor route can
 * actually produce. The rest — a login refusal, a taken name — cannot reach
 * here, and `unauthenticated` should not either: the caller routes that to the
 * shell, which ends the session. They fall to one sentence rather than being
 * enumerated, because a panel that says nothing at all is the failure this
 * function exists to prevent, and inventing a distinct sentence for a state
 * nobody can reach would be inventing behaviour nobody can check.
 */
export const describeVendorFailure = (refusal: ApiRefusal): string => {
  switch (refusal.kind) {
    case 'vendor-refused':
      return describeVendorRefusal(refusal.reason);
    case 'no-such-character':
      return 'This character is no longer on your account, so the vendor has nobody to trade with.';
    case 'rate-limited':
      return 'That was a lot of asking at once. Wait a moment and try again.';
    case 'unreachable':
      return 'The vendor is not answering. Try again in a moment.';
    default:
      return 'The vendor could not do that. Leave town and come back, then try again.';
  }
};

/** The one sentence the last exchange became. Every figure in it is the API's. */
export const vendorNoteText = (note: VendorNote): string => {
  switch (note.kind) {
    case 'bought':
      return `Bought ${note.name} for ${countLabel(note.spent)} gold.`;
    case 'sold':
      return `Sold ${note.name} for ${countLabel(note.received)} gold.`;
    case 'repaired':
      return `Repaired ${note.name} for ${countLabel(note.spent)} gold.`;
    case 'refused':
      return describeVendorFailure(note.refusal);
  }
};

/** Whether an exchange is already on its way. */
export const isExchanging = (pending: VendorPending | null | undefined): boolean =>
  pending !== null && pending !== undefined;

/** Whether *this* control is the one that was pressed. Both halves have to match. */
export const isPendingRow = (
  pending: VendorPending | null | undefined,
  kind: ExchangeKind,
  id: string,
): boolean => pending?.kind === kind && pending.id === id;

/**
 * The exchange to start, or `null` because one is already in flight.
 *
 * **Any** exchange blocks any other, not merely a second press of the same
 * button: two decisions made against one purse are one decision too many, and
 * the second was taken against a balance this client has not been told about
 * yet.
 */
export const startExchange = (
  pending: VendorPending | null | undefined,
  next: VendorPending,
): VendorPending | null => (isExchanging(pending) ? null : next);

/**
 * The one line the panel says out loud.
 *
 * An exchange in flight wins over the news of the last one, for the reason
 * `statusNoteOf` gives about a run that is opening: the press that produced this
 * render *is* the answer to whatever came before it, and two live sentences in
 * one polite region are read out as a single run-on line.
 *
 * Empty string rather than `null`, so the region keeps its identity across
 * renders and a reader actually announces it.
 */
export const vendorStatusOf = (
  pending: VendorPending | null | undefined,
  note: VendorNote | null | undefined,
): string => {
  if (isExchanging(pending)) return ASKING_NOTE;

  return note === null || note === undefined ? '' : vendorNoteText(note);
};

/**
 * A control's accessible name: the verb, the thing, and the figure.
 *
 * Fifteen buttons reading *Buy* name nothing, and the figure belongs in the name
 * rather than only in the row beside it, because that number is what the press
 * is a decision about.
 */
export const exchangeName = (kind: ExchangeKind, name: string, gold: number): string => {
  const verb = kind === 'buy' ? 'Buy' : kind === 'sell' ? 'Sell' : 'Repair';

  return `${verb} ${name} for ${countLabel(gold)} gold`;
};

/** What the control reads while it is being answered, so a press is visibly taken. */
export const exchangeLabelOf = (kind: ExchangeKind, busy: boolean): string => {
  if (kind === 'buy') return busy ? 'Buying…' : 'Buy';
  if (kind === 'sell') return busy ? 'Selling…' : 'Sell';

  return busy ? 'Repairing…' : 'Repair';
};
